import React, { useState, useContext, useRef } from "react";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import styles from "./popupcard.module.css";
import { DataContext } from "../../context/dataprovider.js";
import usePaymentRequest from "../../components/paymentmethods.js";
import {
  handleFileChanges,
  handleRemoveFile,
} from "../../components/helperfunctions.js";
import {
  calculateNetPayable,
  getPendingAmount,
} from "../../utils/paymentAmounts.js";
const PopupCard = ({ open, onClose, data, userData, page, filters }) => {
  // ℹ️ No useEffect needed — PopupCard gets key={trans_id} from parent,
  // so React fully remounts it for each new transaction (all state is fresh automatically).
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [declineReasonVisible, setDeclineReasonVisible] = useState(false);
  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [selectedDeclineReason, setSelectedDeclineReason] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [partialAmount, setPartialAmount] = useState(0);
  const { statusColors, statusDef, role } = useContext(DataContext);
  const [specifyDecilne, setSpecifyDecline] = useState("");
  const theme = useTheme();
  const [customReason, setCustomReason] = useState("");
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { updateStatus, updateSuspend } = usePaymentRequest();
  const fileInputRef = useRef(null);

  if (!data) return null;

  // Derive pickFiles and field directly — no state or useEffect needed
  let pickFiles = false;
  let field = "";
  if (
    (((data.status === statusDef.phase4 && userData.email === data.sender_id) ||
      data.status === statusDef.qualityfail) &&
      data.sender_id === userData.email) ||
    (userData.role === role.roles_4 && data.status === statusDef.phase4) ||
    (userData.role === role.roles_4 && data.status === statusDef.qualityfail)
  ) {
    field = "uploadReceipts";
    pickFiles = true;
  } else if (
    (data.status === statusDef.phase3 || data.status === statusDef.partialPayment) &&
    role.roles_4 === userData.role
  ) {
    field = "paymentDone";
    pickFiles = true;
  }
  const {
    trans_id,
    project_id,
    payment_type,
    sender_name,
    sender_id,
    pr_remarks,
    financial_remarks,
    bank_id,
    dev_id,
    upi_id,
    status,
    rejectedcause,
    acc_bills,
    pr_receipts,
    pr_attachments,
    createdAt,
    updatedAt,
    accountnumber,
    ifsc,
    ponumber,
    vendorname,
    amount,
    tdsApplicable,
    tdsType,
    tdsValue,
    otherDeductions,
    invoice_no,
    inv_date,
    pi_invoice_no,
    pi_date,
    paymentdone_date,
    net_payable,
  } = data;

  // Format the netPayable value to 2 decimal points
  const handleSpecifyDecline = (e) => {
    setSpecifyDecline(e.target.value);
  };
  const handleImageClick = (url) => {
    setSelectedImage(url);
  };

  const handleCloseImageModal = () => {
    setSelectedImage(null);
  };

  const handleDeclineReasonChange = (event) => {
    setSelectedDeclineReason(event.target.value);
    if (event.target.value !== "Others") {
      setCustomReason("");
      setSpecifyDecline("");
    }
  };

  const handleCustomReasonChange = (event) => {
    setCustomReason(event.target.value);
  };
  const handleConfirmDecline = () => {
    let finalReason = selectedDeclineReason;
    // If "Others" is selected, use the custom reason
    if (specifyDecilne?.length > 0) {
      finalReason += ` (${specifyDecilne})`;
    }
    if (selectedDeclineReason === "Other's") {
      finalReason = customReason;
    }
    handleStatus(false, {
      rejectedcause: finalReason,
      permitteby: {
        id: userData.email,
        name: userData.name,
      },
    });
    setDeclineReasonVisible(false);
  };
  // console.log(`user Data in Popup card ${JSON.stringify(userData)}`);

  const getStatusStyles = (currentStatus) => {
    switch (currentStatus) {
      case statusDef.initial:
        return statusColors.initial;
      case statusDef.phase1:
        return statusColors.phase1;
      case statusDef.phase2:
        return statusColors.phase2;
      case statusDef.phase3:
        return statusColors.phase3;
      case statusDef.phase4:
        return statusColors.phase4;
      case statusDef.phase5:
        return statusColors.phase5;
      case statusDef.partialPayment:
        return statusColors.partialPayment;
      case statusDef.final:
        return statusColors.final;
      case statusDef.fail:
        return statusColors.fail;
      case statusDef.accountantDenied:
        return statusColors.accountantDenied;
      case statusDef.financialDenied:
        return statusColors.financialDenied;
      case statusDef.qualityfail:
        return statusColors.qualityfail;
      case statusDef.Suspend:
        return statusColors.suspend;
      default:
        return "#D32F2F"; // Default Red for undefined statuses
    }
  };

  const handleStatus = async (action, additionalData = {}) => {
    const formData = new FormData();
    formData.append("id", data.trans_id);
    formData.append("action", action);
    formData.append("currentStatus", data.status);
    formData.append("name", userData.name);
    formData.append("project_id", data.project_id);
    // Append additional data to the FormData as a JSON string
    formData.append("data", JSON.stringify(additionalData));

    if (additionalData.acc_bills) {
      additionalData.acc_bills?.forEach((file) => {
        if (file.source === "new") {
          formData.append("files", file.file);
        } else {
          formData.append("files", file.url);
        }
      });
    }
    // during payment done
    if (additionalData.partialAmount > 0) {
      formData.append("partialAmount", additionalData.partialAmount);
    }

    if (additionalData.selectedPaymentType && additionalData.selectedPaymentType.length > 0) {
      formData.append("selectedPaymentType", additionalData.selectedPaymentType);
    }

    if (additionalData.pr_receipts) {
      additionalData.pr_receipts.forEach((file) => {
        if (file.source === "new") {
          formData.append("files", file.file);
        } else {
          formData.append("files", file.url);
        }
      });
      if (Array.isArray(pr_receipts).length > 0) {
        // console.log("pr_receipts line 193 popup_card", pr_receipts);
        pr_receipts.forEach((file) => {
          formData.append("files", file); // Append each file to the "files" field
        });
      }
    }
    if (additionalData.permitteby) {
      formData.append("permitteby", JSON.stringify(additionalData.permitteby));
    }
    if (additionalData.rejectedcause) {
      formData.append("rejectedcause", additionalData.rejectedcause);
    }

    const response = await updateStatus.mutateAsync(formData);

    // }
    setSelectedFiles([]);
    onClose();
  };

  const handleSuspend = async () => {
    // console.log(userData.name);
    const response = await updateSuspend.mutateAsync({
      id: data.trans_id,
      name: userData.name,
      project_id: data.project_id,
    });
    setSelectedFiles([]);
    onClose();
  };

  const handleStatusWithImages = async () => {
    try {
      // console.log(selectedFiles);
      if (selectedFiles?.length === 0) {
        alert("Please select files to upload");
        return;
      }
      if (field === "paymentDone") {
        // const updatedacc_bills = imageUrls.concat(data.acc_bills || []);
        handleStatus(true, {
          acc_bills: selectedFiles,
          partialAmount: partialAmount ? partialAmount : 0,
          selectedPaymentType: selectedPaymentType,
          permitteby: {
            id: userData.email,
            name: userData.name,
          },
        }); // Pass files to upload
        return;
      }

      if (field === "uploadReceipts") {
        // const updatedpr_receipts = imageUrls.concat(data.pr_receipts || []);
        handleStatus(true, {
          pr_receipts: selectedFiles,
          permitteby: {
            id: userData.email,
            name: userData.name,
          },
        }); // Pass files to upload
        return;
      }
    } catch (error) {
      console.error("Error sending data:", error);
      alert("Error setting status");
    }
  };

  const renderButtons = () => {
    if (status === statusDef.Suspend) {
      // onClose();
      return null;
    }

    if (
      (status === statusDef.initial && userData.role === role.roles_2) ||
      (userData.role === role.roles_5 && status === statusDef.initial)
    ) {
      return (
        <>
          <Button
            onClick={() =>
              handleStatus(true, {
                permitteby: { id: userData.email, name: userData.name },
              })
            }
            variant="contained"
            style={{ backgroundColor: "#4CAF50", color: "#FFFFFF" }}
          >
            Accept
          </Button>
          <Button
            onClick={() => setDeclineReasonVisible(true)}
            variant="contained"
            style={{ backgroundColor: "#F44336", color: "#FFFFFF" }}
          >
            Decline
          </Button>
        </>
      );
    }

    if (
      (status === statusDef.phase1 && role.roles_3 === userData.role) ||
      (status === statusDef.phase1 && role.roles_5 === userData.role)
    ) {
      return (
        <>
          <Button
            onClick={() =>
              handleStatus(true, {
                permitteby: { id: userData.email, name: userData.name },
              })
            }
            variant="contained"
            style={{ backgroundColor: "#4CAF50", color: "#FFFFFF" }}
          >
            Accept
          </Button>
          <Button
            onClick={() => setDeclineReasonVisible(true)}
            variant="contained"
            style={{ backgroundColor: "#F44336", color: "#FFFFFF" }}
          >
            Deny
          </Button>
        </>
      );
    }

    if (status === statusDef.phase2 && role.roles_4 === userData.role) {
      return (
        <>
          <Button
            onClick={() =>
              handleStatus(true, {
                permitteby: { id: userData.email, name: userData.name },
              })
            }
            variant="contained"
            style={{ backgroundColor: "#4CAF50", color: "#FFFFFF" }}
          >
            Upload to Bank
          </Button>
          <Button
            onClick={() => setDeclineReasonVisible(true)}
            variant="contained"
            style={{ backgroundColor: "#F44336", color: "#FFFFFF" }}
          >
            Decline
          </Button>
        </>
      );
    }

    if (
      (status === statusDef.phase3 || status === statusDef.partialPayment) &&
      role.roles_4 === userData.role
    ) {
      return (
        <>
          <Button
            onClick={() => {
              setPaymentDialogVisible(true);
              // handleStatusWithImages();
            }}
            variant="contained"
            style={{ backgroundColor: "#4CAF50", color: "#FFFFFF" }}
          >
            Payment Done
          </Button>
          <Button
            onClick={() => setDeclineReasonVisible(true)}
            variant="contained"
            style={{ backgroundColor: "#F44336", color: "#FFFFFF" }}
          >
            Decline
          </Button>
        </>
      );
    }

    if (
      (status === statusDef.phase4 || status === statusDef.qualityfail) &&
      (data.sender_id === userData.email || userData.role === role.roles_4)
    ) {
      return (
        <>
          <Button
            onClick={() => {
              handleStatusWithImages(); // Correctly call the function here
            }}
            variant="contained"
            style={{ backgroundColor: "#4CAF50", color: "#FFFFFF" }}
          >
            Upload Receipts
          </Button>
        </>
      );
    }

    if (status === statusDef.phase5 && role.roles_4 === userData.role) {
      return (
        <>
          <Button
            onClick={() =>
              handleStatus(true, {
                permitteby: { id: userData.email, name: userData.name },
              })
            }
            variant="contained"
            style={{ backgroundColor: "#4CAF50", color: "#FFFFFF" }}
          >
            Accept
          </Button>
          <Button
            onClick={() => setDeclineReasonVisible(true)}
            variant="contained"
            style={{ backgroundColor: "#F44336", color: "#FFFFFF" }}
          >
            Decline
          </Button>
        </>
      );
    }

    if (status === statusDef.qualityfail && role.roles_4 === userData.role) {
      return (
        <Button
          onClick={handleSuspend}
          variant="contained"
          style={{ backgroundColor: "#4CAF50", color: "#FFFFFF" }}
        >
          Suspend
        </Button>
      );
    }

    return null;
  };

  return (
    <>
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          fullScreen={fullScreen}
          classes={{ paper: styles.dialogPaper }}
        >
          <DialogTitle className={styles.dialogHeader}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" className={styles.title}>
                {data.payment_type}
              </Typography>
              <IconButton onClick={onClose} className={styles.closeButton}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent className={styles.content}>
            {/* Sender Details */}

            <Box className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Sender Details
              </Typography>
              <Box className={styles.detailContainer}>
                <Box className={styles.detailColumn}>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Sender Name:</strong>
                    <span className={styles.detailValue}>
                      {data.sender_name}
                    </span>
                  </Typography>
                </Box>

                <Box className={styles.detailColumn}>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>
                      Sender email:
                    </strong>
                    <span className={styles.sender_id}>{data.sender_id}</span>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Transaction Details */}
            <Box className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Transaction Details
              </Typography>
              <Box className={styles.detailContainer}>
                <Box className={styles.detailColumn}>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Amount:</strong>
                    <span className={styles.detailValue}>{data.amount}</span>
                  </Typography>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Invoice Type:</strong>
                    <span className={styles.detailValue}>
                      {data.invoice_type || "N/A"}
                    </span>
                  </Typography>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Details:</strong>
                    <span className={styles.detailValue}>
                      {data.pr_remarks}
                    </span>
                  </Typography>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Status:</strong>
                    <Chip
                      label={data.status}
                      className={styles.statusChip}
                      style={{
                        backgroundColor: getStatusStyles(data.status).bg,
                        color: getStatusStyles(data.status).text,
                      }}
                    />
                  </Typography>
                  {data.account_head && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        Accountant Head:
                      </strong>
                      <span className={styles.detailValue}>
                        {data.account_head}
                      </span>
                    </Typography>
                  )}
                  {data.invoice_no && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        Invoice no:
                      </strong>
                      <span className={styles.detailValue}>
                        {data.invoice_no}
                      </span>
                    </Typography>
                  )}
                  {data.tds_applicable && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        TDS Applicable:
                      </strong>
                      <span className={styles.detailValue}>
                        {data.tds_applicable}
                      </span>
                    </Typography>
                  )}
                  {data.tds_type && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>TDS Type:</strong>
                      <span className={styles.detailValue}>
                        {data.tds_type}
                      </span>
                    </Typography>
                  )}
                  {/* {data.net_payable !== undefined && ( */}
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Net Payment:</strong>
                    <span className={styles.detailValue}>
                      {calculateNetPayable(data)}
                    </span>
                  </Typography>
                  {/* )} */}
                </Box>

                <Box className={styles.detailColumn}>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Uploaded:</strong>
                    <span className={styles.detailValue}>
                      {new Date(data.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </span>
                  </Typography>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Project:</strong>
                    <span className={styles.detailValue}>
                      {data.project_id}
                    </span>
                  </Typography>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>
                      Transaction ID:
                    </strong>
                    <span className={styles.detailValue}>{data.trans_id}</span>
                  </Typography>
                  {data.tds_value && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>TDS Value:</strong>
                      <span className={styles.detailValue}>
                        {data.tds_value}
                      </span>
                    </Typography>
                  )}
                  {data.other_deductions && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        Other Deductions:
                      </strong>
                      <span className={styles.detailValue}>
                        {data.other_deductions}
                      </span>
                    </Typography>
                  )}
                  {data.inv_date && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>Inv Date:</strong>
                      <span className={styles.detailValue}>
                        {new Date(data.inv_date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </span>
                    </Typography>
                  )}
                  {data.pi_invoice_no && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        Proforma Inv No:
                      </strong>
                      <span className={styles.detailValue}>
                        {data.pi_invoice_no}
                      </span>
                    </Typography>
                  )}
                  {data.pi_date && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>PI Date:</strong>
                      <span className={styles.detailValue}>
                        {new Date(data.pi_date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </span>
                    </Typography>
                  )}
                  {data.paymentdone_date && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        Payment Done Date:
                      </strong>
                      <span className={styles.detailValue}>
                        {new Date(data.paymentdone_date).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider className={styles.divider} />

            {data.pr_remarks?.length !== 0 && (
              <>
                <Box className={styles.section}>
                  <Typography className={styles.remarksText}>
                    <strong>PR Remarks:</strong>
                    <span>{data.pr_remarks}</span>
                  </Typography>
                </Box>
                <Divider className={styles.divider} />
              </>
            )}

            {/* Rejected Cause */}
            {data.rejectedcause && (
              <>
                <Box className={styles.section}>
                  <Typography className={styles.rejectedText}>
                    <strong>Rejected Cause:</strong>
                    <span>{data.rejectedcause}</span>
                  </Typography>
                </Box>
                <Divider className={styles.divider} />
              </>
            )}

            {/* Financial Remarks */}
            {data.financial_remarks?.length !== 0 && (
              <>
                <Box className={styles.section}>
                  <Typography className={styles.remarksText}>
                    <strong>Financial Remarks:</strong>
                    <span>{data.financial_remarks}</span>
                  </Typography>
                </Box>
                <Divider className={styles.divider} />
              </>
            )}

            {/* Bank Details */}
            <Box className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Bank Details
              </Typography>
              <Box className={styles.detailContainer}>
                <Box className={styles.detailColumn}>
                  <Typography className={styles.detailItem}>
                    <strong className={styles.detailLabel}>Vendor Name:</strong>
                    <span className={styles.detailValue}>
                      {data.vendorname || "-"}
                    </span>
                  </Typography>
                  {data.Branch && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>Branch:</strong>
                      <span className={styles.detailValue}>{data.Branch}</span>
                    </Typography>
                  )}
                  {data.accountnumber && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        Account Number:
                      </strong>
                      <span className={styles.detailValue}>
                        {data.accountnumber}
                      </span>
                    </Typography>
                  )}
                  {data.upinumber && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>
                        UPI Number:
                      </strong>
                      <span className={styles.detailValue}>
                        {data.upinumber}
                      </span>
                    </Typography>
                  )}
                </Box>

                <Box className={styles.detailColumn}>
                  {data.ponumber && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>PO Number:</strong>
                      <span className={styles.detailValue}>
                        {data.ponumber}
                      </span>
                    </Typography>
                  )}
                  {data.po_date && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>PO Date:</strong>
                      <span className={styles.detailValue}>
                        {new Date(data.po_date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </span>
                    </Typography>
                  )}
                  {data.ifsc && (
                    <Typography className={styles.detailItem}>
                      <strong className={styles.detailLabel}>IFSC Code:</strong>
                      <span className={styles.detailValue}>{data.ifsc}</span>
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider className={styles.divider} />

            {/* Attachments */}
            <Box className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Attachments
              </Typography>
              <Box className={styles.attachmentsContainer}>
                {pr_attachments && pr_attachments.length > 0 ? (
                  pr_attachments.map((url, index) => {
                    // Check if the URL includes any of the image extensions
                    const imageExtensions = [
                      "jpg",
                      "jpeg",
                      "png",
                      "gif",
                      "webp",
                    ];
                    let isImage = imageExtensions.some((ext) =>
                      url.includes(ext)
                    );

                    // If the URL is an image, keep it as is; otherwise, strip the query parameters
                    let newurl = isImage ? url : url.split("?")[0];

                    // Check for 'isImage' query parameter
                    if (!isImage && url.includes("?")) {
                      const params = new URLSearchParams(url.split("?")[1]);
                      if (params.get("isImage") === "true") {
                        isImage = true;
                      }
                    }

                    // console.log(newurl, "this is new url", isImage);

                    return (
                      <Box
                        position="relative"
                        key={index}
                        marginRight="10px"
                        marginBottom="10px"
                      >
                        {isImage ? (
                          <img
                            src={newurl}
                            alt={`Attachment ${index + 1}`}
                            onClick={() => handleImageClick(newurl)}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                          />
                        ) : (
                          <a
                            href={newurl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: "100px",
                              height: "100px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px dashed gray",
                              textDecoration: "none",
                              color: "black",
                            }}
                          >
                            <DescriptionIcon
                              style={{ fontSize: "40px", color: "gray" }}
                            />
                            <span
                              style={{ marginTop: "5px", fontSize: "14px" }}
                            >
                              View Document
                            </span>
                          </a>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Typography className={styles.noFiles}>
                    No attachments available
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Accountant Bills */}
            <Box className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Accountant Bills
              </Typography>
              <Box className={styles.attachmentsContainer}>
                {Array.isArray(acc_bills) && acc_bills.length > 0 ? (
                  acc_bills.map((url, index) => {
                    const imageExtensions = [
                      "jpg",
                      "jpeg",
                      "png",
                      "gif",
                      "webp",
                    ];
                    let isImage = imageExtensions.some((ext) =>
                      url.includes(ext)
                    );
                    let newurl = isImage ? url : url.split("?")[0];
                    if (!isImage && url.includes("?")) {
                      const params = new URLSearchParams(url.split("?")[1]);
                      if (params.get("isImage") === "true") {
                        isImage = true;
                      }
                    }

                    // console.log(newurl, "this is new url", isImage);

                    return (
                      <Box
                        position="relative"
                        key={index}
                        marginRight="10px"
                        marginBottom="10px"
                      >
                        {isImage ? (
                          <img
                            src={newurl}
                            alt={`Attachment ${index + 1}`}
                            onClick={() => handleImageClick(newurl)}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                          />
                        ) : (
                          <a
                            href={newurl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: "100px",
                              height: "100px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px dashed gray",
                              textDecoration: "none",
                              color: "black",
                            }}
                          >
                            <DescriptionIcon
                              style={{ fontSize: "40px", color: "gray" }}
                            />
                            <span
                              style={{ marginTop: "5px", fontSize: "14px" }}
                            >
                              View Document
                            </span>
                          </a>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Typography className={styles.noFiles}>
                    No bills available
                  </Typography>
                )}
              </Box>
            </Box>

            {/* PR Receipts */}
            <Box className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                PR Receipts
              </Typography>
              <Box className={styles.attachmentsContainer}>
                {Array.isArray(pr_receipts) && pr_receipts.length > 0 ? (
                  pr_receipts.map((url, index) => {
                    const imageExtensions = [
                      "jpg",
                      "jpeg",
                      "png",
                      "gif",
                      "webp",
                    ];
                    let isImage = imageExtensions.some((ext) =>
                      url.includes(ext)
                    );
                    let newurl = isImage ? url : url.split("?")[0];
                    if (!isImage && url.includes("?")) {
                      const params = new URLSearchParams(url.split("?")[1]);
                      if (params.get("isImage") === "true") {
                        isImage = true;
                      }
                    }

                    // console.log(newurl, "this is new url", isImage);

                    return (
                      <Box
                        position="relative"
                        key={index}
                        marginRight="10px"
                        marginBottom="10px"
                      >
                        {isImage ? (
                          <img
                            src={newurl}
                            alt={`Attachment ${index + 1}`}
                            onClick={() => handleImageClick(newurl)}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                          />
                        ) : (
                          <a
                            href={newurl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: "100px",
                              height: "100px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px dashed gray",
                              textDecoration: "none",
                              color: "black",
                            }}
                          >
                            <DescriptionIcon
                              style={{ fontSize: "40px", color: "gray" }}
                            />
                            <span
                              style={{ marginTop: "5px", fontSize: "14px" }}
                            >
                              View Document
                            </span>
                          </a>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Typography className={styles.noFiles}>
                    No receipts available
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>

          {/* File Upload Section */}
          {pickFiles && (
            <>
              <Divider className={styles.divider} />
              <Box className={styles.fileUploadSection}>
                <Button
                  variant="outlined"
                  component="label"
                  className={styles.uploadButton}
                >
                  Pick Files
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={(event) =>
                      handleFileChanges(event, selectedFiles, setSelectedFiles)
                    }
                  />
                </Button>

                <Typography className={styles.fileCounter}>
                  {selectedFiles?.length > 0
                    ? `${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""
                    } selected`
                    : "No files selected"}
                </Typography>
              </Box>
            </>
          )}

          {/* Selected Files Preview */}
          {Array.isArray(selectedFiles) && selectedFiles?.length > 0 && (
            <>
              <Divider className={styles.divider} />
              <Box className={styles.selectedFilesSection}>
                <Typography className={styles.sectionTitle} variant="h6">
                  Selected Files:
                </Typography>
                <Box
                  className={styles.attachmentsContainer}
                  display="flex"
                  flexWrap="wrap"
                >
                  {selectedFiles.map((item, index) => {
                    const previewUrl =
                      item.source === "new" && item.file
                        ? URL.createObjectURL(item.file) // Create preview for new files
                        : item.url; // Use the preloaded URL directly

                    return (
                      <Box
                        position="relative"
                        key={index}
                        marginRight="10px"
                        marginBottom="10px"
                      >
                        {item.isImage ? (
                          <img
                            src={previewUrl}
                            alt={item.name}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: "100px",
                              height: "100px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px dashed gray",
                              textDecoration: "none",
                              color: "black",
                            }}
                          >
                            <DescriptionIcon
                              style={{ fontSize: "40px", color: "gray" }}
                            />
                            <span
                              style={{ marginTop: "5px", fontSize: "14px" }}
                            >
                              View Document
                            </span>
                          </a>
                        )}
                        <IconButton
                          onClick={(event) =>
                            handleRemoveFile(setSelectedFiles, item.name)
                          }
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            color: "red",
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}

          <DialogActions className={styles.dialogActions}>
            {renderButtons()}
          </DialogActions>
        </Dialog>

      {/* Image Preview Modal */}
      {selectedImage && (
        <Dialog
          open={true}
          onClose={handleCloseImageModal}
          classes={{ paper: styles.imagePreview }}
        >
          <img
            src={selectedImage}
            alt="Preview"
            className={styles.fullSizeImage}
          />
        </Dialog>
      )}

      {/* Decline Reason Modal */}
      {declineReasonVisible && (
        <Dialog
          open={declineReasonVisible}
          onClose={() => setDeclineReasonVisible(false)}
        >
          <DialogTitle>Decline Transaction</DialogTitle>
          <DialogContent>
            <FormControl fullWidth>
              <InputLabel>Reason for Decline</InputLabel>
              <Select
                value={selectedDeclineReason}
                onChange={handleDeclineReasonChange}
              >
                {/* Map through the decline reasons and render a MenuItem for each */}
                {userData?.declinereasons?.map((reason, index) => (
                  <MenuItem key={index} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
              {selectedDeclineReason !== "Other's" && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Specify Reason"
                  value={specifyDecilne}
                  onChange={handleSpecifyDecline}
                />
              )}
            </FormControl>
            {selectedDeclineReason === "Other's" && (
              <TextField
                fullWidth
                margin="normal"
                label="Enter custom reason"
                value={customReason}
                onChange={handleCustomReasonChange}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleConfirmDecline}
              variant="contained"
              color="primary"
              disabled={
                !selectedDeclineReason ||
                (selectedDeclineReason === "Others" &&
                  !customReason &&
                  !specifyDecilne)
              }
            >
              Confirm Decline
            </Button>
            <Button
              onClick={() => setDeclineReasonVisible(false)}
              variant="contained"
              color="secondary"
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {paymentDialogVisible && (
        <Dialog
          open={paymentDialogVisible}
          onClose={() => setPaymentDialogVisible(false)}
        >
          <DialogTitle>Process Payment</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={selectedPaymentType}
                onChange={(e) => setSelectedPaymentType(e.target.value)}
              >
                <MenuItem value="full">Full Payment</MenuItem>
                <MenuItem value="partial">Partial Payment</MenuItem>
              </Select>
            </FormControl>

            {/* Notes / Amount Section */}
            {selectedPaymentType && (
              <>
                {selectedPaymentType === "partial" && (
                  <TextField
                    fullWidth
                    margin="normal"
                    type="number"
                    label="Partial Payment Amount"
                    value={partialAmount}
                    onChange={(e) => setPartialAmount(e.target.value)}
                  />
                )}
                {selectedPaymentType === "full" && (
                  <TextField
                    fullWidth
                    margin="normal"
                    type="number"
                    label="Full Payment Amount (Remaining)"
                    value={getPendingAmount(data)}
                    disabled // user cannot edit
                  />
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => handleStatusWithImages()}
              variant="contained"
              color="primary"
              disabled={
                !selectedPaymentType ||
                (selectedPaymentType === "partial" && !partialAmount)
              }
            >
              Confirm Payment
            </Button>
            <Button
              onClick={() => setPaymentDialogVisible(false)}
              variant="contained"
              color="secondary"
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}



    </>
  );
};
export default PopupCard;
